import { Challenge, FrequencyPattern } from '@/types/challenge'
import { simpleNotificationManager } from '../../packages/@2cmstudio/notifications/SimpleNotificationManager'

/**
 * Check if a paused challenge should be auto-resumed
 */
export function shouldAutoResume(challenge: Challenge): boolean {
  if (challenge.status !== 'paused' || !challenge.pauseUntil) {
    return false
  }

  const now = new Date()
  const pauseUntil = new Date(challenge.pauseUntil)
  
  return now >= pauseUntil
}

/**
 * Calculate next notification times for a challenge based on its frequency
 */
export function calculateNextOccurrences(
  frequency: FrequencyPattern,
  hoursAhead: number = 24
): string[] {
  const now = new Date()
  const endTime = new Date(now.getTime() + hoursAhead * 60 * 60 * 1000)
  const occurrences: string[] = []
  
  switch (frequency.mode) {
    case 'daily':
      if (frequency.times) {
        for (let day = new Date(now); day <= endTime; day.setDate(day.getDate() + 1)) {
          frequency.times.forEach(time => {
            const [hours, minutes] = time.split(':').map(Number)
            const occurrence = new Date(day)
            occurrence.setHours(hours, minutes, 0, 0)
            
            if (occurrence > now && occurrence <= endTime) {
              occurrences.push(occurrence.toISOString())
            }
          })
        }
      }
      break
      
    case 'weekly':
      if (frequency.daysOfWeek && frequency.times) {
        for (let day = new Date(now); day <= endTime; day.setDate(day.getDate() + 1)) {
          if (frequency.daysOfWeek.includes(day.getDay())) {
            frequency.times.forEach(time => {
              const [hours, minutes] = time.split(':').map(Number)
              const occurrence = new Date(day)
              occurrence.setHours(hours, minutes, 0, 0)
              
              if (occurrence > now && occurrence <= endTime) {
                occurrences.push(occurrence.toISOString())
              }
            })
          }
        }
      }
      break
      
    case 'hourly':
      if (frequency.startHour !== undefined && frequency.endHour !== undefined && frequency.times) {
        for (let day = new Date(now); day <= endTime; day.setDate(day.getDate() + 1)) {
          for (let hour = frequency.startHour; hour <= frequency.endHour; hour++) {
            frequency.times.forEach(time => {
              const [minutes] = time.split(':').map(Number)
              const occurrence = new Date(day)
              occurrence.setHours(hour, minutes, 0, 0)
              
              if (occurrence > now && occurrence <= endTime) {
                occurrences.push(occurrence.toISOString())
              }
            })
          }
        }
      }
      break
      
    case 'custom':
      // For custom mode, we'll just use the times for today and tomorrow
      if (frequency.times) {
        for (let day = new Date(now); day <= endTime; day.setDate(day.getDate() + 1)) {
          frequency.times.forEach(time => {
            const [hours, minutes] = time.split(':').map(Number)
            const occurrence = new Date(day)
            occurrence.setHours(hours, minutes, 0, 0)
            
            if (occurrence > now && occurrence <= endTime) {
              occurrences.push(occurrence.toISOString())
            }
          })
        }
      }
      break
  }
  
  return occurrences.sort()
}

/**
 * Simple motivational phrases for challenge reminders
 */
const MOTIVATIONAL_PHRASES = [
  "Time to stay on track! 💪",
  "You've got this! Keep going! 🎯",
  "Another step towards your goal! ⭐",
  "Stay consistent, stay strong! 🚀",
  "Your future self will thank you! 🌟",
  "Small steps, big results! 📈",
  "Keep up the great work! 👏",
  "Progress, not perfection! ✨"
]

/**
 * Get a random motivational phrase
 */
function getRandomMotivationalPhrase(): string {
  return MOTIVATIONAL_PHRASES[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.length)]
}

/**
 * Schedule local notifications for a challenge
 */
export async function scheduleNotificationsForChallenge(challenge: Challenge): Promise<void> {
  if (challenge.status !== 'active') {
    return
  }

  try {
    const occurrences = calculateNextOccurrences(challenge.frequency, 24)
    const now = new Date()
    
    let scheduledCount = 0
    
    for (const occurrence of occurrences) {
      const occurrenceTime = new Date(occurrence)
      const delaySeconds = Math.max(0, (occurrenceTime.getTime() - now.getTime()) / 1000)
      
      // Only schedule if the time is in the future
      if (delaySeconds > 0) {
        const phrase = getRandomMotivationalPhrase()
        
        try {
          await simpleNotificationManager.sendNotification(
            {
              title: challenge.title,
              body: phrase,
              icon: '/icon-192x192.png',
              data: {
                type: 'challenge_reminder',
                challengeId: challenge.id,
                challengeTitle: challenge.title,
                scheduledFor: occurrence
              }
            },
            delaySeconds
          )
          scheduledCount++
        } catch (error) {
          console.error(`Failed to schedule notification for ${occurrence}:`, error)
        }
      }
    }
    
    if (import.meta.env.DEV) {
      console.log(`✅ Scheduled ${scheduledCount}/${occurrences.length} notifications for challenge: ${challenge.title}`)
    }
  } catch (error) {
    console.error('Failed to schedule notifications for challenge:', challenge.title, error)
  }
}

/**
 * Schedule notifications for multiple challenges
 */
export async function scheduleNotificationsForChallenges(challenges: Challenge[]): Promise<void> {
  const activeChallenges = challenges.filter(c => c.status === 'active')
  
  if (activeChallenges.length === 0) {
    if (import.meta.env.DEV) {
      console.log('No active challenges to schedule notifications for')
    }
    return
  }

  if (import.meta.env.DEV) {
    console.log(`📅 Scheduling notifications for ${activeChallenges.length} active challenges`)
  }
  
  for (const challenge of activeChallenges) {
    await scheduleNotificationsForChallenge(challenge)
  }
  
  if (import.meta.env.DEV) {
    console.log('✅ Finished scheduling notifications for all active challenges')
  }
}

/**
 * Reschedule all notifications for challenges
 */
export async function rescheduleAllNotifications(challenges: Challenge[]): Promise<void> {
  try {
    // Clear all existing notifications
    await simpleNotificationManager.clearAllScheduledNotifications()
    
    // Schedule new notifications
    await scheduleNotificationsForChallenges(challenges)
    
    if (import.meta.env.DEV) {
      console.log('✅ All notifications rescheduled successfully')
    }
  } catch (error) {
    console.error('Failed to reschedule notifications:', error)
  }
}

/**
 * Update notifications when a challenge is modified
 */
export async function updateNotificationsForChallenge(
  oldChallenge: Challenge,
  newChallenge: Challenge
): Promise<void> {
  try {
    // Clear old notifications
    await simpleNotificationManager.clearNotificationsForChallenge(oldChallenge.id)
    
    // Schedule new notifications if challenge is still active
    if (newChallenge.status === 'active') {
      await scheduleNotificationsForChallenge(newChallenge)
    }
    
    if (import.meta.env.DEV) {
      console.log(`✅ Updated notifications for challenge: ${newChallenge.title}`)
    }
  } catch (error) {
    console.error(`Failed to update notifications for challenge ${newChallenge.title}:`, error)
  }
}

/**
 * Cancel notifications for a specific challenge
 */
export async function cancelNotificationsForChallenge(challenge: Challenge): Promise<void> {
  try {
    await simpleNotificationManager.clearNotificationsForChallenge(challenge.id)
    if (import.meta.env.DEV) {
      console.log(`✅ Cancelled notifications for challenge: ${challenge.title}`)
    }
  } catch (error) {
    console.error(`Failed to cancel notifications for challenge ${challenge.title}:`, error)
  }
}

/**
 * Check if notification system is ready
 */
export function isNotificationSystemReady(): boolean {
  return simpleNotificationManager.isNotificationSupported() && 
         simpleNotificationManager.getPermissionStatus() === 'granted'
}

/**
 * Get notification system status
 */
export function getNotificationSystemStatus() {
  return simpleNotificationManager.getStatus()
}

/**
 * Get challenges that should be auto-resumed
 */
export function getChallengesForAutoResume(challenges: Challenge[]): Challenge[] {
  return challenges.filter(shouldAutoResume)
}

/**
 * Format pause duration for display
 */
export function formatPauseDuration(challenge: Challenge): string {
  if (!challenge.pauseUntil) {
    return ''
  }

  const now = new Date()
  const pauseUntil = new Date(challenge.pauseUntil)
  const diffMs = pauseUntil.getTime() - now.getTime()

  if (diffMs <= 0) {
    return 'Resume now'
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

  if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes}m`
  } else {
    return `${diffMinutes}m`
  }
}

/**
 * Get time until resume for a paused challenge
 */
export function getTimeUntilResume(challenge: Challenge): string | null {
  if (!challenge.pauseUntil) {
    return null
  }

  const now = new Date()
  const pauseUntil = new Date(challenge.pauseUntil)
  
  if (now >= pauseUntil) {
    return null // Should be resumed
  }

  return formatPauseDuration(challenge)
} 